import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle, Lock, Dumbbell, Sparkles, Target,
  BookOpen, ShoppingCart, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DIENSTEN = [
  {
    key: "vrije_training",
    naam: "Vrije Training",
    omschrijving: "Zelf losse trainingen samenstellen en bijhouden",
    prijs: 25,
    icon: <Dumbbell size={16} />,
    bezit: (u) => u?.vrije_training_enabled === true,
    koopLink: "/bestellen?product=vrije_training",
  },
  {
    key: "schema_builder_unlock",
    naam: "Eigen Schemamaker",
    omschrijving: "Zelf volledige trainingsschema's maken en opslaan",
    prijs: 100,
    icon: <Sparkles size={16} />,
    bezit: (u) => u?.schema_builder_unlocked === true,
    koopLink: "/bestellen?product=schema_builder_unlock",
  },
  {
    key: "persoonlijk_schema",
    naam: "Persoonlijk Trainingsschema",
    omschrijving: "Op maat gemaakt schema van jouw coach",
    prijs: 9.95,
    prijsLabel: "vanaf €9,95",
    icon: <Target size={16} />,
    bezit: (u) =>
      Array.isArray(u?.purchased_schemas) && u.purchased_schemas.length > 0,
    koopLink: "/bestellen?product=persoonlijk_schema",
  },
  {
    key: "cursus",
    naam: "Cursus Pro",
    omschrijving: "Volledige online cursus met lessen, toetsen en certificaat",
    prijs: 399.95,
    icon: <BookOpen size={16} />,
    bezit: (u) => u?.plan === "full",
    koopLink: "/bestellen?product=cursus",
  },
];

function fmt(prijs) {
  return `€${prijs % 1 === 0 ? prijs : prijs.toFixed(2).replace(".", ",")}`;
}

export default function MijnAankopenSection({ user }) {
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState(null);
  const token = user?.token;

  const eigendom  = DIENSTEN.filter((d) => d.bezit(user));
  const beschikbaar = DIENSTEN.filter((d) => !d.bezit(user));

  const handleSnelKopen = async (dienst) => {
    setLoadingKey(dienst.key);
    try {
      const r = await fetch(`${API}/api/payments/bundle-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items: [{ product: dienst.key }] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      window.location.href = d.checkout_url;
    } catch {
      navigate(dienst.koopLink);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="mijn-aankopen-section">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold text-base">Mijn diensten</h3>
        <span className="text-zinc-600 text-xs">
          {eigendom.length}/{DIENSTEN.length} actief
        </span>
      </div>

      {/* Eigendom */}
      {eigendom.length > 0 && (
        <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5">
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Actief</p>
          </div>
          {eigendom.map((d) => (
            <div key={d.key} className="flex items-center gap-3 px-4 py-3.5 border-b border-white/4 last:border-b-0"
              data-testid={`dienst-actief-${d.key}`}>
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 text-emerald-400">
                {d.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold">{d.naam}</p>
                <p className="text-zinc-600 text-xs">{d.omschrijving}</p>
              </div>
              <CheckCircle size={16} className="text-emerald-400 shrink-0" />
            </div>
          ))}
        </div>
      )}

      {/* Nog niet gekocht */}
      {beschikbaar.length > 0 && (
        <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5">
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Beschikbaar</p>
          </div>
          {beschikbaar.map((d) => (
            <div key={d.key}
              className="flex items-center gap-3 px-4 py-3.5 border-b border-white/4 last:border-b-0"
              data-testid={`dienst-beschikbaar-${d.key}`}>
              <div className="w-8 h-8 rounded-lg bg-white/4 flex items-center justify-center shrink-0 text-zinc-600">
                {d.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-zinc-300 text-sm font-semibold">{d.naam}</p>
                <p className="text-zinc-600 text-xs">{d.omschrijving}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-zinc-400 text-xs font-bold">{d.prijsLabel || fmt(d.prijs)}</span>
                <button
                  onClick={() => handleSnelKopen(d)}
                  disabled={loadingKey === d.key}
                  data-testid={`koop-${d.key}-btn`}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-black transition-opacity disabled:opacity-50"
                  style={{ background: "#C9A84C" }}>
                  {loadingKey === d.key ? (
                    <div className="w-3 h-3 border border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><ShoppingCart size={11} /><span>Kopen</span></>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {eigendom.length === DIENSTEN.length && (
        <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-500/8 border border-emerald-500/20 rounded-xl px-4 py-3">
          <CheckCircle size={15} /> Je hebt toegang tot alle diensten!
        </div>
      )}
    </div>
  );
}
